import { CheckIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import PageTransition from '../../../components/animations/PageTransition';
import ErrorState from '../../../components/states/ErrorState';
import SkeletonTable from '../../../components/states/SkeletonTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useDeleteCreditPack } from '../hooks/useDeleteCreditPack';
import { CreditPackDto, useListCreditPacks } from '../hooks/useListCreditPacks';
import { useUpdateConversionRate } from '../hooks/useUpdateConversionRate';
import { tableProps, tableThProps, tableThFlexProps, tableCellProps, tableHeaderTextProps } from 'theme/constants';
import { useUpdateCreditPack } from '../hooks/useUpdateCreditPack';
import { formatCents } from '../utils/credit-pack.utils';
import CreateCreditPackModal from './CreateCreditPackModal';
import EditCreditPackModal from './EditCreditPackModal';

const columnHelper = createColumnHelper<CreditPackDto>();

export default function CreditPackList() {
  const { selectedOrganization } = useSelectedOrganization();
  const { data, isInitialLoading, isError } = useListCreditPacks(
    selectedOrganization?.id || '',
  );
  // `useListCreditPacks` is `enabled: !!organizationId` and this page carried NO
  // scope guard, so the raw `isLoading` it drew its skeleton from stayed `true`
  // forever whenever no organization was selected — a permanent skeleton table
  // over a read nobody sent. Classify off the query's own flags, never from a
  // restatement of the gate (D62).
  const readState = classifyRead(isError, isInitialLoading, data);
  const [sorting, setSorting] = useState<SortingState>([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [editingPack, setEditingPack] = useState<CreditPackDto | null>(null);

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [deletingPack, setDeletingPack] = useState<CreditPackDto | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const { mutateAsync: deletePack, isLoading: isDeleting } =
    useDeleteCreditPack();
  const { mutateAsync: updatePack } = useUpdateCreditPack();
  const { mutateAsync: updateRate, isLoading: isSavingRate } =
    useUpdateConversionRate();

  const savedRate = selectedOrganization?.creditConversionRate ?? 1;
  const [conversionRateStr, setConversionRateStr] = useState<string>(
    String(savedRate),
  );
  const conversionRate = parseFloat(conversionRateStr) || 0;
  const rateHasChanged = conversionRate !== savedRate;

  useEffect(() => {
    setConversionRateStr(String(savedRate));
  }, [savedRate]);

  const handleSaveConversionRate = async () => {
    try {
      await updateRate(conversionRate);
      toast({
        title: translate('credit_pack.conversion_rate_saved'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: translate('credit_pack.update_error'),
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEdit = (pack: CreditPackDto) => {
    setEditingPack(pack);
    onEditOpen();
  };

  const handleDelete = (pack: CreditPackDto) => {
    setDeletingPack(pack);
    onDeleteOpen();
  };

  const handleToggleActive = async (pack: CreditPackDto) => {
    try {
      await updatePack({ id: pack.id, isActive: !pack.isActive });
      toast({
        title: pack.isActive
          ? translate('credit_pack.deactivated')
          : translate('credit_pack.activated'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: translate('credit_pack.update_error'),
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingPack) return;
    try {
      await deletePack(deletingPack.id);
      toast({
        title: translate('credit_pack.deleted_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setDeletingPack(null);
    } catch (error: any) {
      toast({
        title: translate('credit_pack.delete_error'),
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('credit_pack.name')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('priceInCents', {
      id: 'price',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('credit_pack.price')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {formatCents(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('credits', {
      id: 'credits',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('credit_pack.credits')}
        </Text>
      ),
      cell: (info) => (
        <Badge colorScheme="green" fontSize="sm">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('sortOrder', {
      id: 'sortOrder',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('credit_pack.sort_order')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('isActive', {
      id: 'isActive',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('credit_pack.active')}
        </Text>
      ),
      cell: (info) => (
        <Switch
          isChecked={info.getValue()}
          onChange={(e) => {
            e.stopPropagation();
            handleToggleActive(info.row.original);
          }}
          colorScheme="green"
        />
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('credit_pack.actions')}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center" gap={2}>
          <IconButton
            aria-label={translate('credit_pack.edit')}
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            colorScheme="brand"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(info.row.original);
            }}
          />
          <IconButton
            aria-label={translate('credit_pack.delete')}
            icon={<DeleteIcon />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(info.row.original);
            }}
          />
        </Flex>
      ),
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [textColor]);

  const table = useReactTable({
    data: data || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (readState === 'never-issued') {
    return (
      <PageCard title={translate('credit_pack.credit_packs')}>
        <ScopePrompt gate='organization' />
      </PageCard>
    );
  }

  if (readState === 'loading') {
    return (
      <PageCard title={translate('credit_pack.credit_packs')}>
        <SkeletonTable rows={5} columns={6} />
      </PageCard>
    );
  }

  // `unreadable`, not the raw `isError`: a BACKGROUND refetch that fails over
  // rows already on screen must keep them rather than blank the table (R96-3).
  if (readState === 'unreadable') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ErrorState message={translate('credit_pack.load_error')} />
      </Card>
    );
  }
  return (
    <PageTransition>
    <>
    <Card flexDirection="column" w="100%" px="0px" mb="20px">
      <Flex px="25px" py="20px" align="flex-end" gap={4}>
        <FormControl maxW="240px">
          <FormLabel fontSize="sm" color="gray.500">
            {translate('credit_pack.conversion_rate')}
          </FormLabel>
          <NumberInput
            value={conversionRateStr}
            onChange={(valueStr) => setConversionRateStr(valueStr)}
            min={0}
            step={0.01}
            precision={2}
            size="sm"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
        <Text fontSize="sm" color="gray.500" pb="8px">
          1 credit = {conversionRate} EUR
        </Text>
        {rateHasChanged && (
          <Button
            size="sm"
            colorScheme="brand"
            leftIcon={<CheckIcon />}
            isLoading={isSavingRate}
            onClick={handleSaveConversionRate}
          >
            {translate('credit_pack.save')}
          </Button>
        )}
      </Flex>
    </Card>
    <PageCard
      title={translate('credit_pack.credit_packs')}
      rightHeader={
        <Flex align="center" gap={3}>
          {data && data.length > 0 && (
            <Text fontSize="xs" color="gray.500">{data.length} packs</Text>
          )}
          <Button colorScheme="brand" onClick={onCreateOpen} size="sm">
            {translate('credit_pack.create_pack')}
          </Button>
        </Flex>
      }
    >
      <Box>
        <Table {...tableProps}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    {...tableThProps}
                    key={header.id}
                    borderColor={borderColor}
                    cursor="pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Flex
                      {...tableThFlexProps}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      <SortIndicator direction={header.column.getIsSorted()} />
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    {...tableCellProps}
                    borderColor={borderColor}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
        {(!data || data.length === 0) && (
          <Flex justify="center" py={8}>
            <Text color="gray.400">
              {translate('credit_pack.no_packs')}
            </Text>
          </Flex>
        )}
      </Box>

      <CreateCreditPackModal isOpen={isCreateOpen} onClose={onCreateClose} />

      {editingPack && (
        <EditCreditPackModal
          pack={editingPack}
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setEditingPack(null);
          }}
        />
      )}

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelButtonRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {translate('credit_pack.delete_pack')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {translate('credit_pack.delete_confirm').replace(
                '{name}',
                deletingPack?.name || '',
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                {translate('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={isDeleting}
              >
                {translate('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageCard>
    </>
    </PageTransition>
  );
}
